import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type Props = {
  open: boolean;
  submitting?: boolean;
  /** When true, copy reads as rename instead of first-time save. */
  rename?: boolean;
  defaultFileName?: string;
  defaultName?: string;
  onClose: () => void;
  onSubmit: (payload: { fileName: string; name: string }) => void;
};

/** First character of the trimmed string only — e.g. "my look" → "My look". */
const capitalizeLibraryTitle = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const ImageGeneratorLibraryModal: React.FC<Props> = ({
  open,
  submitting,
  rename = false,
  defaultFileName = "",
  defaultName = "",
  onClose,
  onSubmit,
}) => {
  const [fileName, setFileName] = useState(defaultFileName);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFileName(defaultFileName || defaultName);
  }, [defaultFileName, defaultName, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, submitting]);

  if (!open) {
    return null;
  }

  const trimmed = fileName.trim();
  const canSave = Boolean(trimmed) && !submitting;

  const handleSave = () => {
    const titled = capitalizeLibraryTitle(trimmed);
    setFileName(titled);
    onSubmit({ fileName: titled, name: titled });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-[#1F1F1F]/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={rename ? "Rename library file" : "Save to library"}
      onClick={() => {
        if (!submitting) {
          onClose();
        }
      }}
    >
      <div
        className="w-full max-w-[360px] rounded-[16px] bg-white px-5 py-5 shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        <h2 className="mb-4 font-poppins text-base font-semibold text-secondary">
          {rename ? "Rename" : "Save to Library"}
        </h2>
        <label className="mb-6 block">
          <span className="mb-2 block font-poppins text-[10px] font-normal leading-[135%] tracking-[0.015em] text-[#4D4D4D]">
            File Name
          </span>
          <input
            value={fileName}
            onChange={event => setFileName(event.target.value)}
            placeholder="Name"
            maxLength={120}
            autoFocus
            className="h-10 w-full rounded-lg border border-[#D0D0D0] px-3 text-sm font-normal text-[#4D4D4D] placeholder:text-[#4D4D4D] focus:outline-none"
          />
        </label>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-normal text-[#999999]">
            <span className="mr-0.5">*</span>
            {rename ? "Update library name" : "Save to Library"}
          </p>
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            className="h-10 shrink-0 rounded-full bg-black px-8 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageGeneratorLibraryModal;
